/**
 * Sticky cart bar for PDP.
 *
 * Always visible from page load (live parity: `.index-product #cart-bar`'s
 * `!important` opacity/visibility override in live's custom CSS — a
 * deliberate merchant choice, not scroll-triggered). Shows the product title,
 * price and an Add to cart button. Multi-variant products open a size-picker
 * popup; single-variant products add directly.
 *
 * Adds are not handled here. The direct-add and picker markup are real
 * `/cart/add` forms, and assets/cart.js delegates their submit to Cart.add() —
 * same request, same drawer, same `cart:change` event as the main buy box. This
 * element only manages visibility, the picker popup, and closing it after an add.
 *
 * The bar's price follows the chosen subscription purchase option: it mirrors
 * `selling-plan:change` (component-selling-plans.js) and re-reads the widget's
 * current selection on connect, so toggling one-time / Subscribe & Save updates
 * the bar alongside the PDP price block.
 */
class CartBarPDP extends HTMLElement {
  /** Guards against double-binding when the element is re-parented. */
  #listenersBound = false;

  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onCartChange = this.onCartChange.bind(this);
    this.onCartError = this.onCartError.bind(this);
    this.onSellingPlanChange = this.onSellingPlanChange.bind(this);
    this.onWidgetChange = this.onWidgetChange.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    if (!this.#listenersBound) {
      this.#listenersBound = true;
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('cart:change', this.onCartChange);
      document.addEventListener('cart:error', this.onCartError);
      document.addEventListener('selling-plan:change', this.onSellingPlanChange);
      document.addEventListener('change', this.onWidgetChange);
    }

    this.observeBuyButtons();

    /* The widget's initial price sync fires while this script is still loading
       (its module precedes ours), so read the current selection directly. */
    this.syncFromWidget();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.reserveScrollSpace(false);

    if (!this.#listenersBound) return;

    this.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onSubmit);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('cart:error', this.onCartError);
    document.removeEventListener('selling-plan:change', this.onSellingPlanChange);
    document.removeEventListener('change', this.onWidgetChange);
    this.#listenersBound = false;
  }

  /**
   * @returns {HTMLElement | null}
   */
  get picker() {
    return this.querySelector('[data-cart-bar-picker]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  get trigger() {
    return this.querySelector('[data-cart-bar-trigger]');
  }

  /**
   * Always visible on desktop, live parity: `.index-product #cart-bar`
   * carries `opacity: 1 !important; visibility: visible !important;` in
   * live's custom CSS, an explicit merchant override that keeps the bar up
   * from page load rather than revealing it once the buy box scrolls out of
   * view. No scroll listener needed — just add the class once and leave it.
   * @returns {void}
   */
  observeBuyButtons() {
    this.classList.add('is-visible');
    this.reserveScrollSpace(true);

    // The strip hides below 750px and can change height, so keep the reserved space in sync.
    if ('ResizeObserver' in window && this.bar) {
      this.resizeObserver ??= new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.bar);
    }
  }

  /**
   * @returns {HTMLElement | null} The fixed strip (the host itself has no height).
   */
  get bar() {
    return this.querySelector('.cart-bar');
  }

  /**
   * @returns {void}
   */
  onResize() {
    this.reserveScrollSpace(true);
  }

  /**
   * Reserves the strip's height at the bottom of the scrollport so focused controls scroll into view above it.
   * Shared with the mobile bar (component-cart-bar-mobile.js), so it only clears space it reserved itself.
   * @param {boolean} visible - Whether the bar is currently shown
   * @returns {void}
   */
  reserveScrollSpace(visible) {
    const root = document.documentElement;
    const height = visible ? this.bar?.offsetHeight || 0 : 0;

    if (height) {
      root.style.scrollPaddingBlockEnd = `${height}px`;
      root.dataset.scrollReserveOwner = this.id;
    } else if (root.dataset.scrollReserveOwner === this.id) {
      root.style.scrollPaddingBlockEnd = '';
      delete root.dataset.scrollReserveOwner;
    }
  }

  /**
   * Delegated clicks inside the bar: toggle or close the size picker.
   * @param {MouseEvent} event
   * @returns {void}
   */
  onClick(event) {
    if (event.target.closest('[data-cart-bar-picker-close]')) {
      this.closePicker();
      return;
    }

    if (event.target.closest('[data-cart-bar-trigger]')) {
      this.togglePicker();
    }
  }

  /**
   * Close the picker when clicking away from it and the trigger.
   * @param {MouseEvent} event
   * @returns {void}
   */
  onDocumentClick(event) {
    const picker = this.picker;
    if (!picker || picker.hidden) return;
    if (picker.contains(event.target) || this.trigger?.contains(event.target)) return;
    this.closePicker();
  }

  /**
   * Clear a previous failure before cart.js posts the retry.
   * @param {SubmitEvent} event
   * @returns {void}
   */
  onSubmit(event) {
    if (!(event.target instanceof HTMLFormElement)) return;
    event.target.querySelector('.cart-bar__picker-item')?.classList.remove('has-error');
  }

  /**
   * A successful add (from any source) closes the picker so the drawer is clear.
   * @returns {void}
   */
  onCartChange() {
    this.closePicker();
  }

  /**
   * Mark the size that failed, leaving the picker open so it can be retried or a
   * different size chosen. `detail.source` is the form cart.js posted.
   * @param {CustomEvent} event
   * @returns {void}
   */
  onCartError(event) {
    const item = event.detail?.source?.querySelector?.('.cart-bar__picker-item');
    if (!item || !this.contains(item)) return;
    item.classList.add('has-error');
  }

  /**
   * The purchase option changed (one-time vs Subscribe & Save, or the variant
   * changed) — mirror the chosen price into the bar. `detail` carries the same
   * presentment strings the PDP price block now shows.
   * @param {CustomEvent} event
   * @returns {void}
   */
  onSellingPlanChange(event) {
    const { price, compareAtPrice } = event.detail ?? {};
    if (!price) return;
    this.syncPrice(price, compareAtPrice);
  }

  /**
   * Belt-and-suspenders fallback: any change inside the selling-plans widget
   * re-reads its current selection straight off the DOM. Covers every path where
   * the `selling-plan:change` custom event could be missed — e.g. the widget's
   * own handler failing before it dispatches, or a third-party script stopping
   * propagation — and keeps the bar correct no matter what.
   * @param {Event} event
   * @returns {void}
   */
  onWidgetChange(event) {
    this.syncPriceFromWidget(event.target.closest?.('selling-plans-widget'));
  }

  /**
   * @returns {Element | null}
   */
  get priceCurrent() {
    return this.querySelector('.cart-bar__price-current');
  }

  /**
   * @returns {Element | null}
   */
  get priceCompare() {
    return this.querySelector('.cart-bar__price-compare');
  }

  /**
   * Update the bar's price — and the sale/compare pair when the chosen option
   * carries a compare-at price — to match the PDP price block.
   * @param {string} price
   * @param {string} [compareAtPrice]
   * @returns {void}
   */
  syncPrice(price, compareAtPrice) {
    const current = this.priceCurrent;
    if (!current) return;

    current.textContent = price;

    const onSale = Boolean(compareAtPrice) && compareAtPrice !== price;
    current.classList.toggle('cart-bar__price--sale', onSale);

    let compare = this.priceCompare;
    if (onSale && !compare) {
      compare = document.createElement('s');
      compare.className = 'cart-bar__price-compare';
      current.insertAdjacentElement('afterend', compare);
    }
    if (compare) {
      compare.textContent = onSale ? compareAtPrice : '';
      compare.hidden = !onSale;
    }
  }

  /**
   * Re-read the selling-plans widget's current selection so the bar's price is
   * correct even if its initial `selling-plan:change` fired before this element
   * connected.
   * @returns {void}
   */
  syncFromWidget() {
    this.syncPriceFromWidget(this.closest('product-info')?.querySelector('selling-plans-widget'));
  }

  /**
   * Centralise the "read the widget's current selection and mirror it" logic so
   * the initial connect, the custom-event path, and the change fallback all agree.
   * @param {Element | null} widget
   * @returns {void}
   */
  syncPriceFromWidget(widget) {
    if (!widget) return;
    const option = widget.selectedPurchaseOption;
    if (option?.dataset.variantPrice) {
      this.syncPrice(option.dataset.variantPrice, option.dataset.variantCompareAtPrice);
    }
  }

  /**
   * @returns {void}
   */
  togglePicker() {
    const picker = this.picker;
    if (!picker) return;

    if (picker.hidden) {
      this.openPicker();
      return;
    }

    this.closePicker();
  }

  /**
   * @returns {void}
   */
  openPicker() {
    const picker = this.picker;
    if (!picker) return;
    picker.hidden = false;
    this.trigger?.setAttribute('aria-expanded', 'true');
  }

  /**
   * @returns {void}
   */
  closePicker() {
    const picker = this.picker;
    if (!picker || picker.hidden) return;
    picker.hidden = true;
    this.trigger?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('cart-bar-pdp')) {
  customElements.define('cart-bar-pdp', CartBarPDP);
}
